import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/services/axios';
import createCompany from '@/API/Company/createCompany';
import setCompanyDataStorageLocation from '@/API/Company/setCompanyDataStorageLocation';
import { FORM_FIELD_PATHS } from '../constants';

const useCreateCompany = (
    formData,
    isAddMode,
    resetToMainTable,
    redirectToNewCompany,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            let compCreated = false;
            let compData = null;
            const compCreatedError =
                "The company has been created, but could not be approved. Please come back to the Companies menu for approval at later time. We are redirecting you to the new company's  dashboard. ";
            const couldNotCreateError =
                'Could not Create Company at this time. Please try again later.';
            const locationID =
                formData?.[FORM_FIELD_PATHS.DATA_STORAGE_LOCATION_ID];
            try {
                const createCompResp = await createCompany(formData);

                compCreated = true;
                compData = createCompResp;

                // The company POST does not persist dataStorageLocationID —
                // it must be set once via its own dedicated endpoint.
                if (locationID) {
                    try {
                        await setCompanyDataStorageLocation(
                            createCompResp?.data?.id,
                            locationID,
                        );
                    } catch (locationError) {
                        console.error(
                            'Error setting Data Residency Region:',
                            locationError,
                        );
                        alert(
                            'The company has been created, but the Data Residency Region could not be set. Please set it from the Edit Company page.',
                        );
                    }
                }
                if (formData?.isTrial) {
                    const companyApprovalData = await axios.get(
                        `/company/${createCompResp?.data?.id}/approval/`,
                    );
                    if (companyApprovalData.data?.canApprove) {
                        await axios.put(
                            `/company/${createCompResp?.data?.id}/approval?trial=true`,
                            companyApprovalData.data,
                        );
                    } else {
                        alert(compCreatedError);
                    }
                }
                if (redirectToNewCompany) {
                    redirectToNewCompany(createCompResp);
                }
                return createCompResp.data;
            } catch (e) {
                alert(compCreated ? compCreatedError : couldNotCreateError);
                if (compCreated && redirectToNewCompany) {
                    redirectToNewCompany(compData);
                }
                throw e;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['companies']);
            if (resetToMainTable) {
                resetToMainTable();
            }
        },
        onError: (error) => {
            console.error('Error creating company:', error);
        },
        staleTime: 0,
    });
};

export default useCreateCompany;
